import React from 'react';
import PropTypes from 'prop-types';
import { PlayerProfileLink } from 'widgets/statspro/js/components/common/PlayerProfileLink';
import { BenchReasonLabel } from './BenchReasonLabel';

/**
 * Renders the Player name table data cell.
 *
 * @param {object} props - Component props.
 * @param {object} props.cell - The react-table cell object.
 * @param {object} props.cellProps - Render props for the td element.
 * @param {boolean} props.isMainCompetition - Whether to enable profile links.
 * @param {boolean} props.isAFLW - Whether the competition is AFLW.
 * @param {object} props.benchReasonMap - All interchange players' ID and bench reason.
 * @returns {React.JSX.Element} The rendered player data cell.
 */
export const PlayerCell = ({
    cell,
    cellProps,
    isMainCompetition,
    isAFLW,
    benchReasonMap
}) => {
    const hasProfileLink =
        isMainCompetition && cell?.row?.values?.player?.playerId;
    const playerId =
        cell?.row?.values?.player?.playerId ||
        cell?.row?.original?.player?.playerId;
    const rawReason = playerId ? benchReasonMap[playerId] : null;

    const playerName = (
        <>
            {cell.value.givenName} <strong>{cell.value.surname}</strong>
        </>
    );

    return (
        <td {...cellProps} className="stats-table__cell">
            {hasProfileLink ? (
                <PlayerProfileLink
                    classes="mc-player-stats-table__player mc-player-stats-table__player--link"
                    isAFLW={isAFLW}
                    player={{
                        playerId: cell.row.values.player.playerId,
                        firstName: cell.value.givenName,
                        surname: cell.value.surname
                    }}
                    clubId={cell.row.original.teamId}
                >
                    <span className="mc-player-stats-table__name">
                        {playerName}
                    </span>
                    <BenchReasonLabel rawReason={rawReason} />
                </PlayerProfileLink>
            ) : (
                <div className="mc-player-stats-table__player">
                    <span className="mc-player-stats-table__name">
                        {playerName}
                    </span>
                </div>
            )}
        </td>
    );
};

PlayerCell.propTypes = {
    cell: PropTypes.shape({
        value: PropTypes.shape({
            givenName: PropTypes.string,
            surname: PropTypes.string
        }).isRequired,
        row: PropTypes.shape({
            values: PropTypes.shape({
                player: PropTypes.shape({
                    playerId: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number
                    ])
                })
            }),
            original: PropTypes.shape({
                teamId: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number
                ]),
                playerId: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number
                ]),
                player: PropTypes.shape({
                    playerId: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number
                    ])
                })
            })
        })
    }).isRequired,
    cellProps: PropTypes.object.isRequired,
    isMainCompetition: PropTypes.bool.isRequired,
    benchReasonMap: PropTypes.object.isRequired,
    isAFLW: PropTypes.bool.isRequired
};
